/**
 * THE TOOL ROWS AND THE OPTIONS SCHEMA — six tool pages as data, for PLATFORM's exporter to
 * publish and UI's option panel to render.
 *
 * ── S8: WHY THIS FILE CHANGED SHAPE ─────────────────────────────────────────────────
 *
 * It used to emit `{nodes: [...]}` with `kind`-tagged nodes. **UI's renderer refuses it at
 * mount** — `validateSchema` checks `version !== 1` and `!Array.isArray(groups)` before it
 * looks at anything else — so all six pages went live showing *"We couldn't reach the
 * server."* on a page that contacts no server, directly under a badge saying it runs entirely
 * in the browser.
 *
 * C5 is ruled: `{version: 1, groups: [...]}` with `type`-tagged fields, and the deciding
 * argument is that its three special field types were designed from the engine briefs by
 * name. This file now emits that shape. `test/clean/schema.test.ts` reads UI's own source and
 * pins every type and every validator rule, so their next change turns this suite red instead
 * of turning six live pages blank.
 *
 * ── WHAT THE OPTION SURFACE IS FOR ──────────────────────────────────────────────────
 *
 * THE NESTING IS THE POINT. keep-italic, keep-bold and keep-font sit UNDER "remove
 * formatting" as `children`; the pattern lists sit under the switches that enable them.
 * Flattened into a list of twenty checkboxes this becomes the wall the competitor has, which
 * is the one place their UI is genuinely confusing.
 *
 * EVERY LABEL, HELP STRING AND SLUG IS A FOUNDER DECISION. Slugs are the SEO surface; labels
 * are what a user reads. `docs/USER_JOURNEY.md` gives copy for exactly one string this
 * surface touches — the B1 trust line — and it is reproduced VERBATIM in `TRUST_LINE`.
 *
 * `read_formats` is every text format the parser layer accepts, because THE differentiator is
 * that this cleaner is not SRT-only: the competitor's best cleaning page takes SRT alone.
 *
 * US SPELLING throughout, per S8. A product should not disagree with itself, and the label
 * should not disagree with its own URL.
 */

import type { ReadFormat, WriteFormat } from '../../../contracts/cue-model.ts';
import type { FieldType as ContractFieldType } from '../../../contracts/options-schema.ts';
import {
  ACRONYM_MAX_LENGTH,
  LINE_BREAK_DEFAULTS,
  MERGE_SAME_TEXT_THRESHOLD,
  PROFILE_LATIN,
  TICKS_PER_SECOND,
} from './defaults.ts';
import type { PatternEntry } from './patterns.ts';
import { MUSIC_PATTERNS, SOUND_EFFECT_PATTERNS, WATERMARK_PATTERNS } from './patterns.ts';

/** VERBATIM from docs/USER_JOURNEY.md B1. On every tool page; the central trust claim. */
export const TRUST_LINE =
  'Read entirely in your browser — nothing is uploaded. Free, unlimited, no watermark.';

/*───────────────────────────────────────────────────────────────────────────────────────
  The renderable schema — a structural MIRROR of UI's OptionsSchema.

  Deliberately not imported. `web/static/base/src/lib/options-schema.ts` is under UI's tree
  and an engine importing from `web/**` inverts the dependency the whole split exists to
  draw — the base tsconfig excludes `web/` for that reason. So the shape is mirrored here and
  pinned by a test that reads their file, which is the checkable version of "these agree".
  TIMING mirrors it the same way for the same reason.

  The FIELD TYPES are no longer mirrored. `contracts/options-schema.ts` (1.0.0, frozen) holds
  the vocabulary, because TIMING found three independent declarations of it drifting by width —
  a narrowing cannot produce an invalid row, but a mirror cannot survive the list CHANGING: a
  type removed upstream breaks a live page at mount with every suite on both sides green.

  This surface still emits only four of the ten, and that narrowing stays. `Extract` is what
  makes it a narrowing rather than a fourth copy: the four are CHECKED against the contract, so
  a typo is a compile error instead of a page that refuses to mount.

  `validateSchema()` stays in UI's tree and remains the authority on the RULES. It is the types
  that had three copies, not the rule — which is why the cross-seam test below still reads
  their source for the rules, and no longer needs to for the vocabulary.
───────────────────────────────────────────────────────────────────────────────────────*/

/** The four this surface emits, checked against the frozen ten. */
export type FieldType = Extract<ContractFieldType, 'boolean' | 'number' | 'text' | 'select'>;

/**
 * The discriminant carrier, and the reason it exists rather than a bare literal.
 *
 * `Extract` on its own does NOT catch a typo: `Extract<FieldType, 'selct'>` is `never`, which is
 * a silent narrowing to nothing rather than an error — the absence of the error would be the
 * failure, exactly as with `@ts-expect-error`. Constraining through `T extends FieldType` makes
 * a name that is not in the contract's list a compile error AT THE DECLARATION, while keeping
 * `type` a literal so the union still discriminates.
 */
type Emits<T extends FieldType> = { readonly type: T };

export interface Choice {
  readonly value: string;
  readonly label: string;
  readonly help?: string;
}

export interface FieldBase {
  /** Unique across the WHOLE schema, children included — UI's validator enforces it. */
  readonly key: string;
  readonly label: string;
  /** One line under the control. Not a tooltip: the site has no hover-only affordances. */
  readonly help?: string;
  /** Shown indented UNDER this control and only while it is on. */
  readonly children?: readonly OptionField[];
  /** Values of this field that unlock its children. Defaults to `[true]`. */
  readonly childrenWhen?: readonly (string | number | boolean)[];
}

export type OptionField =
  | (FieldBase & Emits<'boolean'> & { readonly default: boolean })
  | (FieldBase &
      Emits<'number'> & {
        readonly default: number | null;
        readonly min?: number;
        readonly max?: number;
        readonly step?: number;
        readonly unit?: string;
      })
  | (FieldBase & Emits<'text'> & { readonly default: string; readonly placeholder?: string })
  | (FieldBase &
      Emits<'select'> & { readonly default: string; readonly choices: readonly Choice[] });

export interface OptionGroup {
  readonly key: string;
  readonly label: string;
  readonly help?: string;
  readonly fields: readonly OptionField[];
  /**
   * Open on first render, and kept out of the phone accordion: the group holding the page's
   * main input. Only the single-purpose pages set it (`opened` below); /clean-subtitles opens
   * none, so its nine groups take turns on a phone.
   */
  readonly open?: boolean;
}

export interface OptionsSchema {
  readonly version: 1;
  readonly groups: readonly OptionGroup[];
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Patterns become nested checkboxes.

  `patterns` was a node kind of this file's own invention and UI has no such field type — an
  unknown type is refused at mount, which is the correct outcome and the reason it is gone. A
  pattern list IS a list of checkboxes, so that is what it emits: one `boolean` per pattern,
  nested under the switch that turns the whole category on.
───────────────────────────────────────────────────────────────────────────────────────*/

function patternFields(prefix: string, entries: readonly PatternEntry[]): readonly OptionField[] {
  return entries.map(
    (entry): OptionField => ({
      type: 'boolean',
      key: `${prefix}.${entry.id}`,
      label: entry.label,
      help: entry.help,
      default: entry.defaultOn,
    }),
  );
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The groups. Composed per tool below, by reference, so a wording fix lands everywhere.
───────────────────────────────────────────────────────────────────────────────────────*/

const HYGIENE: OptionGroup = {
  key: 'hygiene',
  label: 'Always on',
  help: 'Chronological re-sort, duplicate removal, empty-cue removal, and start before end.',
  fields: [
    {
      type: 'boolean',
      key: 'normalize.respectLayers',
      label: 'Treat each language track separately',
      help: 'A ♪♪ cue on each track of a bilingual file is two real cues, not a duplicate.',
      default: true,
    },
    {
      type: 'select',
      key: 'normalize.dropEmpty',
      label: 'Remove empty cues',
      default: 'text-and-drawings',
      choices: [
        {
          value: 'text-and-drawings',
          label: 'Only cues with nothing in them at all',
          help: 'A cue holding a drawn sign, an effect or a comment is kept.',
        },
        { value: 'text-only', label: 'Any cue with no text', help: 'Drawn signs are removed too.' },
        { value: 'never', label: 'Keep every cue' },
      ],
    },
  ],
};

const FORMATTING: OptionGroup = {
  key: 'formatting',
  label: 'Formatting',
  fields: [
    {
      type: 'boolean',
      key: 'stripFormatting.enabled',
      label: 'Remove text formatting',
      default: false,
      children: [
        {
          type: 'select',
          key: 'stripFormatting.scope',
          label: 'Where the formatting lives',
          help:
            'Most files carry their bold and italics on a named style rather than on the text, ' +
            'so "inline tags only" often looks like it did nothing.',
          default: 'both',
          choices: [
            { value: 'both', label: 'Everywhere (recommended)' },
            { value: 'inline', label: 'Inline tags only' },
            { value: 'style', label: 'Named styles only' },
          ],
        },
        {
          type: 'boolean',
          key: 'stripFormatting.keepItalic',
          label: 'Keep italics',
          default: true,
        },
        { type: 'boolean', key: 'stripFormatting.keepBold', label: 'Keep bold', default: false },
        {
          type: 'boolean',
          key: 'stripFormatting.keepUnderline',
          label: 'Keep underline',
          default: false,
        },
        { type: 'boolean', key: 'stripFormatting.keepColor', label: 'Keep colors', default: false },
        {
          type: 'boolean',
          key: 'stripFormatting.keepFont',
          label: 'Keep font and size',
          default: false,
        },
        {
          type: 'boolean',
          key: 'stripFormatting.keepClasses',
          label: 'Keep style class names',
          default: true,
        },
        {
          type: 'boolean',
          key: 'stripFormatting.keepWordTiming',
          label: 'Keep per-word timing',
          help:
            'Turning this off deletes karaoke and word-level timings for good, which disables ' +
            'Enhanced LRC, karaoke ASS and word-level VTT export.',
          default: true,
        },
      ],
    },
  ],
};

const BRACKETS: OptionGroup = {
  key: 'brackets',
  label: 'Brackets',
  fields: [
    {
      type: 'boolean',
      key: 'stripDelimited.enabled',
      label: 'Remove text between brackets',
      default: false,
      children: [
        {
          type: 'boolean',
          key: 'stripDelimited.square',
          label: 'Square brackets  [ ]',
          default: true,
        },
        {
          type: 'boolean',
          key: 'stripDelimited.round',
          label: 'Round brackets  ( )',
          default: true,
        },
        {
          type: 'boolean',
          key: 'stripDelimited.curly',
          label: 'Curly braces  { }',
          help:
            'ASS override tags live in curly braces. They are never part of the text, so this ' +
            'only removes braces a person typed.',
          default: false,
        },
        {
          type: 'boolean',
          key: 'stripDelimited.asterisk',
          label: 'Asterisks  *…*',
          default: false,
        },
        { type: 'boolean', key: 'stripDelimited.hash', label: 'Hashes  #…#', default: false },
        {
          type: 'boolean',
          key: 'stripDelimited.angle',
          label: 'Angle brackets  < >',
          default: false,
        },
        {
          type: 'boolean',
          key: 'stripDelimited.fullWidth',
          label: 'Full-width brackets  （ ）',
          default: false,
        },
        {
          type: 'boolean',
          key: 'stripDelimited.lenticular',
          label: 'Lenticular brackets  【 】',
          default: false,
        },
      ],
    },
  ],
};

const SDH: OptionGroup = {
  key: 'sdh',
  label: 'Captions for the deaf and hard of hearing (SDH)',
  fields: [
    {
      type: 'boolean',
      key: 'removeSdh.enabled',
      label: 'Remove SDH',
      default: false,
      children: [
        {
          type: 'boolean',
          key: 'removeSdh.speakerLabels',
          label: 'Speaker names — "JOHN:", ">> JOHN:"',
          help: 'The dash that marks a second speaker in the same cue is kept; it is punctuation.',
          default: true,
        },
        {
          type: 'boolean',
          key: 'removeSdh.soundEffects',
          label: 'Sound effects',
          default: true,
          children: patternFields('removeSdh.soundEffectPatterns', SOUND_EFFECT_PATTERNS),
        },
        {
          type: 'boolean',
          key: 'removeSdh.musicNotes',
          label: 'Music notes',
          help: 'The notes go and the lyric stays. A cue that was only notes is removed.',
          default: true,
          children: patternFields('removeSdh.musicPatterns', MUSIC_PATTERNS),
        },
        {
          type: 'boolean',
          key: 'removeSdh.watermarksEnabled',
          label: 'Subtitle-site credits and watermarks',
          default: true,
          children: patternFields('removeSdh.watermarks', WATERMARK_PATTERNS),
        },
        {
          type: 'boolean',
          key: 'removeSdh.dropCueIfEmpty',
          label: 'Remove cues that were only an effect',
          default: true,
        },
      ],
    },
  ],
};

const LINES: OptionGroup = {
  key: 'lines',
  label: 'Lines',
  fields: [
    {
      type: 'boolean',
      key: 'unwrap.enabled',
      label: 'Remove line breaks inside cues',
      default: false,
      children: [
        {
          type: 'text',
          key: 'unwrap.joiner',
          label: 'Join with',
          default: ' ',
          placeholder: 'a space',
        },
        {
          type: 'text',
          key: 'unwrap.cjkJoiner',
          label: 'Join with, between Chinese or Japanese characters',
          default: '',
          placeholder: 'nothing',
        },
      ],
    },
    {
      type: 'boolean',
      key: 'reflow.enabled',
      label: 'Rebreak lines',
      default: false,
      children: [
        {
          type: 'number',
          key: 'reflow.maxCells',
          label: 'Maximum line width',
          help: 'Counted the way subtitle guides count it: a Chinese or Japanese character is two.',
          default: LINE_BREAK_DEFAULTS.maxCells,
          min: 10,
          max: 120,
          step: 1,
          unit: 'characters',
        },
        {
          type: 'number',
          key: 'reflow.maxLines',
          label: 'Maximum lines per cue',
          default: LINE_BREAK_DEFAULTS.maxLines,
          min: 1,
          max: 6,
          step: 1,
          unit: 'lines',
        },
        {
          type: 'select',
          key: 'reflow.algorithm',
          label: 'How to break',
          default: 'balanced',
          choices: [
            { value: 'balanced', label: 'Balance the lines (recommended)' },
            { value: 'greedy', label: 'Fill each line first' },
          ],
        },
        {
          type: 'boolean',
          key: 'reflow.breakOnClause',
          label: 'Prefer breaking at punctuation and before conjunctions',
          default: true,
        },
        {
          type: 'boolean',
          key: 'reflow.preserveHardBreaks',
          label: 'Keep deliberate line breaks',
          default: true,
        },
        {
          type: 'select',
          key: 'reflow.overflow',
          label: "When the text won't fit",
          default: 'allow-extra-lines',
          choices: [
            { value: 'allow-extra-lines', label: 'Add a line' },
            { value: 'overflow-last-line', label: 'Let the last line run long' },
            { value: 'split-cue', label: 'Split the cue in two' },
          ],
        },
        {
          type: 'boolean',
          key: 'reflow.cjk',
          label: 'Use Chinese and Japanese line-breaking rules',
          help: 'Stops a line ending on an opening bracket or starting with a full stop.',
          default: true,
        },
      ],
    },
  ],
};

const CAPITALIZATION: OptionGroup = {
  key: 'capitalization',
  label: 'Capitalization',
  fields: [
    {
      type: 'boolean',
      key: 'case.enabled',
      label: 'Change capitalization',
      default: false,
      children: [
        {
          type: 'select',
          key: 'case.mode',
          label: 'To',
          default: 'sentence',
          choices: [
            {
              value: 'sentence',
              label: 'Sentence case',
              help:
                'Capitalizes the first letter of each sentence, including a sentence that carries ' +
                'over from the cue before.',
            },
            { value: 'lower', label: 'lower case' },
            { value: 'upper', label: 'UPPER CASE' },
            { value: 'title', label: 'Title Case' },
          ],
        },
        {
          type: 'boolean',
          key: 'case.continuesAcrossCues',
          label: "Don't capitalize a cue that continues the previous sentence",
          default: true,
        },
        {
          type: 'boolean',
          key: 'case.protectAcronyms',
          label: `Leave acronyms alone (up to ${ACRONYM_MAX_LENGTH} letters)`,
          default: true,
        },
      ],
    },
  ],
};

const DUPLICATES: OptionGroup = {
  key: 'duplicates',
  label: 'Repeated cues',
  fields: [
    {
      type: 'boolean',
      key: 'mergeSameText.enabled',
      label: 'Merge repeated cues',
      default: false,
      children: [
        {
          type: 'number',
          key: 'mergeSameText.threshold',
          label: 'Merge when the gap is under',
          default: Math.round(((MERGE_SAME_TEXT_THRESHOLD as number) / TICKS_PER_SECOND) * 1000),
          min: 0,
          max: 5000,
          step: 10,
          unit: 'ms',
        },
        {
          type: 'select',
          key: 'mergeSameText.compare',
          label: 'Count as the same when',
          default: 'trimmed',
          choices: [
            { value: 'exact', label: 'The text is identical' },
            { value: 'trimmed', label: 'Only spacing differs' },
            { value: 'stripped', label: 'Only spacing, case and punctuation differ' },
          ],
        },
        {
          type: 'boolean',
          key: 'mergeSameText.respectLayers',
          label: 'Never merge across language tracks',
          default: true,
        },
      ],
    },
  ],
};

const FIND_REPLACE: OptionGroup = {
  key: 'findReplace',
  label: 'Find and replace',
  fields: [
    {
      type: 'boolean',
      key: 'replace.enabled',
      label: 'Find and replace',
      default: false,
      children: [
        { type: 'text', key: 'replace.find', label: 'Find', default: '' },
        { type: 'text', key: 'replace.replace', label: 'Replace with', default: '' },
        {
          type: 'boolean',
          key: 'replace.literal',
          label: 'Plain text, not a pattern',
          default: true,
        },
      ],
    },
  ],
};

/** The shared fragile-tag control. Default `skip-cue`, deliberately. */
const ADVANCED: OptionGroup = {
  key: 'advanced',
  label: 'Advanced',
  fields: [
    {
      type: 'select',
      key: 'rawTagPolicy',
      label: 'Cues with animation or unusual tags',
      help:
        'Some ASS cues carry animations or override tags that cannot be re-applied after the ' +
        'text changes. The default leaves those cues alone and tells you how many.',
      default: 'skip-cue',
      choices: [
        { value: 'skip-cue', label: 'Leave them alone (recommended)' },
        {
          value: 'hoist-and-warn',
          label: 'Clean them and keep the tags on the whole cue',
          help: 'Rendering will differ.',
        },
        { value: 'drop-and-warn', label: 'Clean them and discard the tags' },
      ],
    },
  ],
};

const READING_SPEED: OptionGroup = {
  key: 'readingSpeed',
  label: 'Reading speed',
  open: true,
  fields: [
    {
      type: 'select',
      key: 'qc.profile',
      label: 'Reading-speed standard',
      default: 'latin',
      choices: [
        { value: 'latin', label: 'Netflix — English (20 characters/second, 42 per line)' },
        { value: 'latin-children', label: "Netflix — children's (17 characters/second)" },
        { value: 'latin-broadcast', label: 'BBC broadcast (37 characters per line)' },
        { value: 'chinese', label: 'Netflix — Chinese (11 characters/second, 16 per line)' },
        { value: 'japanese', label: 'Netflix — Japanese (13 full-width characters per line)' },
        { value: 'korean', label: 'Netflix — Korean (14 characters/second, 16 per line)' },
      ],
    },
    {
      type: 'number',
      key: 'qc.maxCps',
      label: 'Flag cues faster than',
      default: PROFILE_LATIN.maxCps,
      min: 1,
      max: 40,
      step: 0.5,
      unit: 'characters/second',
    },
    {
      type: 'boolean',
      key: 'qc.countSpaces',
      label: 'Count spaces as characters',
      help: 'Latin guides do; Chinese, Japanese and Korean guides do not.',
      default: PROFILE_LATIN.countSpaces,
    },
  ],
};

function schemaOf(groups: readonly OptionGroup[]): OptionsSchema {
  return { version: 1, groups };
}

/**
 * `group` with its top-level switch `key` defaulting ON, for the page whose
 * whole job is that switch.
 *
 * The groups are shared with /clean-subtitles, where every step starts off and
 * the user picks. On a single-purpose page that default meant the page did
 * nothing: /remove-sdh-from-subtitles, whose copy says it "strips all of it",
 * returned a dropped file unchanged until the user found and ticked "Remove
 * SDH", and /remove-subtitle-formatting ("This takes all of it out") the same
 * with "Remove text formatting". /subtitle-case-converter ("Fix ALL CAPS
 * Subtitles") and /remove-subtitle-line-breaks kept the same bug a round
 * longer, found by dropping a file on each live page on 2026-10-05. Measured
 * through the page's own defaults in test/tools/clean-switches-end-to-end.test.ts.
 */
function switchedOn(group: OptionGroup, key: string): OptionGroup {
  return {
    ...group,
    fields: group.fields.map((f) =>
      f.key === key && f.type === 'boolean' ? { ...f, default: true } : f,
    ),
  };
}

/**
 * `group` marked `open`, for the page whose main input it is.
 *
 * On a phone the option groups are an exclusive accordion (`accordionMembers` in UI's
 * option-panel.ts), and a group the schema opens stays out of it. Left closed, the main group
 * on /remove-sdh-from-subtitles shared the accordion with "Advanced", so opening "Advanced" to
 * finish the job closed the controls the page exists for. The shared groups stay closed on
 * /clean-subtitles, where no one group is the page.
 */
function opened(group: OptionGroup): OptionGroup {
  return { ...group, open: true };
}

/** The whole cleaning surface, for the umbrella tool. */
export const CLEAN_SCHEMA: OptionsSchema = schemaOf([
  HYGIENE,
  FORMATTING,
  BRACKETS,
  SDH,
  LINES,
  CAPITALIZATION,
  DUPLICATES,
  FIND_REPLACE,
  ADVANCED,
]);

/*───────────────────────────────────────────────────────────────────────────────────────
  Tool registry rows
───────────────────────────────────────────────────────────────────────────────────────*/

export interface ToolRow {
  /** BARE — no leading slash. `GET /{slug}` with one routes protocol-relative, off-site. */
  readonly slug: string;
  readonly label: string;
  /** The row's primary operation, for the exporter's per-channel adapter. */
  readonly op: string;
  /**
   * EVERY op the tool applies, IN ORDER — zero (a conversion), one, or many, with no special
   * case. Ruled in S8; `build/tools_export.mjs` reads it ahead of the legacy `op`.
   *
   * REQUIRED as of S9. It was optional for exactly one round, and that was a coordination
   * fact rather than a preference: `ToolRow` is imported by `src/formats/tools.ts`, and
   * making the field required while their eight rows lacked one would have failed THEIR
   * file's typecheck — breaking the tree for another instance over a change in mine.
   * CONVERT landed `ops: []` in `e2ad6e8`, so the condition is met and the field is now
   * required. A row that cannot say which ops it runs is a row nobody can execute.
   */
  readonly ops: readonly string[];
  /** UI's ruled shape (C5). Anything else is refused at mount. */
  readonly options_schema: OptionsSchema;
  readonly read_formats: readonly ReadFormat[];
  readonly write_formats: readonly WriteFormat[];
  /** Pair pages only. Set by the row, never by this engine. */
  readonly from_format?: ReadFormat;
  readonly to_format?: WriteFormat;
  /**
   * PAIR PAGES ONLY — what this conversion costs, as `LossCode`s, derived by
   * CONVERT from the capability rows.
   *
   * Optional because only the five pair rows have one, and `undefined` is a
   * different fact from `[]`: absent means "this is not a pair page", empty
   * means "this conversion is lossless", and that empty case is the best
   * sentence any of those pages carries. Every consumer tests presence, never
   * truthiness.
   */
  readonly loses?: readonly string[];
  /**
   * `'open'` shows the "Read as" encoding panel open on every file, rather than only when the
   * detector is unsure, and leaves the save step's EUC-KR choice off the page. For a page whose
   * whole job is re-reading a file in the right encoding and saving it as UTF-8
   * (/fix-subtitle-encoding). Absent everywhere else, which keeps every other page as it was.
   */
  readonly encoding_panel?: 'open';
}

/**
 * EVERY text format, not just SRT. This is the differentiator and it is a data change rather
 * than a feature: the option surface is identical across all of them because they share one
 * cue model.
 */
export const ALL_TEXT_READ: readonly ReadFormat[] = [
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sub',
  'mpl2',
  'txt',
  'lrc',
  'sbv',
  'json',
];
export const ALL_TEXT_WRITE: readonly WriteFormat[] = [
  'srt',
  'vtt',
  'ass',
  'ssa',
  'smi',
  'sub',
  'txt',
  'lrc',
  'lrc-enhanced',
  'sbv',
  'json',
];

/**
 * The always-on hygiene every cleaning pipeline opens with, and the renumber it closes with.
 * `normalize` is the chronological re-sort, dedupe, clamp and start-before-end pass; ops that
 * change the cue list want `renumber` after them so the file's numbering is not a lie.
 */
const CLEAN_PIPELINE: readonly string[] = [
  'normalize',
  'stripFormatting',
  'stripDelimited',
  'removeSdh',
  'unwrap',
  'case',
  'mergeSameText',
  'replace',
  'reflow',
  'renumber',
];

/**
 * Slugs are the SEO surface, so each row competes for a phrase people type. All six are
 * proposals: DECISION_NEEDED.
 *
 * `op` on the first and last rows is not a registered transform — `clean` is the umbrella for
 * the bundle and `qc` is a separate engine registered through `registerQc`. Both are listed
 * in CORE's `test/core/registry/tool-rows.test.ts` KNOWN_BAD for exactly that reason, and
 * `ops` is what resolves it: once the exporter and that test read `ops`, `op` can be retired
 * on both rows and CORE deletes both entries.
 */
export const CLEAN_TOOLS: readonly ToolRow[] = [
  {
    slug: 'clean-subtitles',
    label: 'Clean up a subtitle file',
    op: 'clean',
    ops: CLEAN_PIPELINE,
    options_schema: CLEAN_SCHEMA,
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
  {
    slug: 'remove-sdh-from-subtitles',
    label: 'Remove SDH, speaker names and sound effects',
    op: 'removeSdh',
    ops: ['normalize', 'removeSdh', 'renumber'],
    options_schema: schemaOf([opened(switchedOn(SDH, 'removeSdh.enabled')), ADVANCED]),
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
  {
    slug: 'subtitle-case-converter',
    label: 'Fix subtitle capitalization',
    op: 'case',
    ops: ['normalize', 'case'],
    options_schema: schemaOf([opened(switchedOn(CAPITALIZATION, 'case.enabled')), ADVANCED]),
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
  {
    slug: 'remove-subtitle-line-breaks',
    label: 'Remove or rebreak subtitle lines',
    op: 'reflow',
    ops: ['normalize', 'unwrap', 'reflow', 'renumber'],
    // Unwrap ON, the job the page is named for. Rewrap stays the user's pick: the two
    // compete, and rewrapping to a width nobody chose is not "removing line breaks".
    options_schema: schemaOf([opened(switchedOn(LINES, 'unwrap.enabled')), ADVANCED]),
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
  {
    slug: 'remove-subtitle-formatting',
    label: 'Remove subtitle formatting',
    op: 'stripFormatting',
    ops: ['normalize', 'stripFormatting', 'stripDelimited', 'renumber'],
    options_schema: schemaOf([
      opened(switchedOn(FORMATTING, 'stripFormatting.enabled')),
      BRACKETS,
      ADVANCED,
    ]),
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
  {
    slug: 'subtitle-reading-speed-checker',
    label: 'Check subtitle reading speed (CPS)',
    op: 'qc',
    // A report, not a transform: it runs `normalize` for the hygiene its numbers assume and
    // then reads. `qc` itself is registered through `registerQc`, not the transform registry.
    ops: ['normalize'],
    options_schema: schemaOf([READING_SPEED]),
    read_formats: ALL_TEXT_READ,
    write_formats: ALL_TEXT_WRITE,
  },
];

/** Kept for the tests and for anyone walking the whole surface. */
export const OPTION_GROUPS = {
  hygiene: HYGIENE,
  formatting: FORMATTING,
  brackets: BRACKETS,
  sdh: SDH,
  lines: LINES,
  capitalization: CAPITALIZATION,
  duplicates: DUPLICATES,
  findReplace: FIND_REPLACE,
  advanced: ADVANCED,
  readingSpeed: READING_SPEED,
} as const;

/** Every field of a schema, depth-first, children included — the shape UI's walker returns. */
export function walkFields(schema: OptionsSchema): readonly OptionField[] {
  const out: OptionField[] = [];
  const stack: OptionField[] = schema.groups.flatMap((g) => [...g.fields]);
  while (stack.length > 0) {
    const field = stack.pop();
    if (field === undefined) continue;
    out.push(field);
    if (field.children !== undefined) stack.push(...field.children);
  }
  return out;
}
